/**
 * Refine an asset with AI: queue a refinement (its status and proposal are the extraction endpoints, polled every
 * 3 s as create from files does), then apply the changes the editor took, as one edit of the asset.
 *
 * Applying answers two kinds of 409, told apart by their headers: `X-Asset-Revision` (the page read an older
 * revision: reload, keep the choices, apply again) and `X-Refine-Changed` (the asset's own text moved since the
 * refinement started, for the change ids it names; nothing was written).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../client';
import type { AssetOut } from '../types/assets';
import type { ExtractionStatusOut } from '../types/extractions';
import type { RefinementApplyIn, RefinementIn } from '../types/refinements';
import { isStaleRevision } from './assets';
import { extractionKeys } from './extractions';
import { queryKeys } from './keys';

/** The change ids a 409 says moved on the asset since the refinement (`X-Refine-Changed`); empty otherwise. */
export function refineChangedIds(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.status !== 409) return [];
  const named = error.headers.get('X-Refine-Changed') ?? '';
  return named.split(',').map((id) => id.trim()).filter(Boolean);
}

/** True for the 409 of a stale revision (`X-Asset-Revision`, or the usual message). */
export function isRevisionConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && (error.headers.has('X-Asset-Revision') || isStaleRevision(error));
}

/** The dialog shows the outcome (a refusal in the dialog, the review page once queued), so no toast. */
export function useStartRefinement(assetId: string) {
  const client = useQueryClient();
  return useMutation({
    meta: { quiet: true },
    mutationFn: (body: RefinementIn) => api<ExtractionStatusOut>(`/api/assets/${assetId}/refinements`, 'POST', body),
    // The asset now names the viewer's refinement (`my_refinement`).
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.asset(assetId) }),
  });
}

/** The review page explains every refusal inline (a reload, the changes that moved, or the reason), so a failure does
 *  not toast as well; the success does. */
export function useApplyRefinement(assetId: string, extractionId: string) {
  const client = useQueryClient();
  return useMutation({
    meta: { success: 'Asset refined', failureShown: true },
    mutationFn: (body: RefinementApplyIn) =>
      api<AssetOut>(`/api/assets/${assetId}/refinements/${extractionId}/apply`, 'POST', body),
    onSuccess: (asset) => {
      client.setQueryData(queryKeys.asset(asset.id), asset);
      client.removeQueries({ queryKey: extractionKeys.proposal(extractionId) });
      void client.invalidateQueries({ queryKey: ['assets', 'list'] });
      void client.invalidateQueries({ queryKey: ['assets', 'history', asset.id] });
      void client.invalidateQueries({ queryKey: queryKeys.partners });
      void client.invalidateQueries({ queryKey: queryKeys.catalogCard });
    },
    // Either 409 means the asset moved: read it again so the page shows its text and revision now.
    onError: (error) => {
      if (refineChangedIds(error).length > 0 || isRevisionConflict(error)) {
        void client.invalidateQueries({ queryKey: queryKeys.asset(assetId) });
      }
    },
  });
}
